import { Folder } from '@dc-shared-web/hooks/api/folder'
import { mapCubeToFolder } from '@dc-shared-web/lib/cubeMappers'
import { useAxiosContext } from '@dc-shared-web/providers'
import { UseQueryOptions, useQuery } from 'react-query'
import { sortObjectsNumerically } from '@dc-shared-web/hooks/utils'
import {
  fetchFolders,
  FetchFoldersParams,
} from '@dc-shared-web/hooks/api/folder/fetchFolders'

// Hook for fetching folders by team
export const useGetFoldersByTeam = (
  params: FetchFoldersParams,
  options?: Omit<UseQueryOptions<Folder[], Error>, 'queryKey' | 'queryFn'>
) => {
  const apiClient = useAxiosContext()
  const nonEmptyParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => Boolean(value))
  )
  //We don't actually know that errors are of type Error, because there could be a network error,
  //but the generic error arg doesn't work with unknown.
  return useQuery<Folder[], Error>(
    ['folders', 'list', nonEmptyParams],
    async () => {
      const folders = await fetchFolders(apiClient, params)
      return folders
        .sort(sortObjectsNumerically('Folder.name'))
        .map(mapCubeToFolder)
    },
    {
      enabled: !!params.teamUuid,
      ...options,
    }
  )
}

// Hook for fetching a single folder by UUID.
//This is a list query that only returns one item, because there is no folder detail endpoint.
export const useGetFolderByUuid = (
  params: FetchFoldersParams,
  options?: Omit<UseQueryOptions<Folder | null, Error>, 'queryKey' | 'queryFn'>
) => {
  const apiClient = useAxiosContext()
  return useQuery<Folder | null, Error>(
    ['folders', 'list', params.teamUuid, params.folderUuid],
    async () => {
      const [folder] = await fetchFolders(apiClient, params)
      //If the folder is not found, return null instead of throwing an error.
      if (!folder) {
        return null
      }
      return mapCubeToFolder(folder)
    },
    {
      enabled: !!params.teamUuid,
      ...options,
    }
  )
}
